/**
 * Company selection  component.
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewEncapsulation,
} from '@angular/core';
import { select } from '@angular-redux/store';
import { ColDef, GridOptions } from 'ag-grid-community';

import { Observable, rx } from '@shared/rx';
import { ICompany, ToolbarButtonDefinition } from '@shared/types';
import { XprtDataActions, SessionActions } from '@store/actions';

@Component({
  selector: 'xvi-company-selection',
  templateUrl: 'company-selection.component.html',
  styleUrls: ['company-selection.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanySelectionComponent {
  @select(['userSession', 'showCompanySelector'])
  public showCompanySelector$: Observable<boolean>;
  @select(['userSession', 'currentCompanyId'])
  public currentCompanyId$: Observable<string>;
  @select(['userSession', 'companies']) public companies$: Observable<
    ICompany[]
  >;

  public selectedItemFromList: any;
  public title: string = 'Select a company:';
  public buttons: ToolbarButtonDefinition[] = [
    {
      caption: 'Done',
      value: 'done',
      disabled: true,
      class: 'btn-primary pull-right',
      click: (): void => this.selectCompany(),
    },
    {
      caption: 'Cancel',
      value: 'cancel',
      class: 'btn-default pull-right',
      click: (): void => void this.cancelButtonClick(),
    },
  ];
  public headerNames: ColDef[] = [
    { headerName: 'Company name', field: 'name', unSortIcon: true },
    { headerName: 'Number of sites', field: 'sites_count', unSortIcon: true },
  ];

  public listGridOptions: GridOptions = {
    defaultColDef: {
      sortable: true,
      resizable: true,
    },
    rowSelection: 'single',
    suppressCellSelection: true,
    onGridSizeChanged: (): void => this.resizeGrid(),
    onModelUpdated: (): void => this.resizeGrid(),
    onRowClicked: (event): void => this.selectItem(event.data),
    onRowDoubleClicked: (): void => this.selectCompany(),
  };

  public constructor(
    private xprtDataActions: XprtDataActions,
    private sessionActions: SessionActions,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  public selectItem(data: unknown | null): void {
    this.selectedItemFromList = data;
    this.buttons = this.buttons.map((b) =>
      b.caption !== 'Done' ? b : { ...b, disabled: !this.selectedItemFromList },
    );
    this.changeDetectorRef.markForCheck();
  }

  public selectCompany(): void {
    if (this.selectedItemFromList) {
      const companyId = this.selectedItemFromList.id;
      this.sessionActions.setActiveCompany(companyId);
    }
  }

  public onListQuickFilterChanged(event): void {
    this.listGridOptions.api?.setQuickFilter(event.target.value);
  }

  public async cancelButtonClick(): Promise<void> {
    if (!(await this.currentCompanyId$.pipe(rx.first()).toPromise())) {
      // logout user after login on cancel button click
      this.sessionActions.logoutUser();
    }
    this.resetForm();
    this.xprtDataActions.hideCompanySelector();
  }

  protected resetForm(): void {
    this.selectItem(null);
  }

  protected resizeGrid(): void {
    this.listGridOptions.api?.sizeColumnsToFit();
  }
}
